import { useEffect, useMemo, useState } from 'react';
import partition from 'lodash/partition';
import { useKnowledgeHub } from '../hooks/use-knowledge-hub.hooks';
import styles from './components.module.scss';
import { Loader } from 'src/components/core';
import { colorPalette, ORGANIZATIONS_LOGO_URL, ORGANIZATIONS_HUB_LOGO_URL } from 'shared/constants';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { Divider } from 'antd';
import { NotFound } from 'src/components/common';

function OrganizationCard({ org, lang, onSelectOrganization }) {
  const department = org.department ? `${lang.DEPARTMENT} ${org.department}` : '';
  const healthSystem = org.healthSystem ? org.healthSystem : org.name;
  // Use cache-busting timestamp from database to force browser to reload updated hub logos
  const cacheBuster = org?.configuration?.hub?.hubLogoUpdatedAt
    ? `?v=${org.configuration.hub.hubLogoUpdatedAt}`
    : '';

  const primaryLogo = ORGANIZATIONS_HUB_LOGO_URL + org.logo + cacheBuster;
  const fallbackLogo = ORGANIZATIONS_LOGO_URL + 'web/' + org.logo;

  const [logoState, setLogoState] = useState('primary');

  const handleImageError = () => {
    setLogoState(prev => (prev === 'primary' ? 'fallback' : 'failed'));
  };

  return (
    <button
      id={org.id}
      key={org.id}
      className={styles.organizationCard}
      onClick={onSelectOrganization}
      type="button"
      aria-label={`View content from ${org.name}`}>
      <div className={styles.organizationLogoWrapper}>
        {logoState !== 'failed' && (
          <img
            className={styles.organizationLogo}
            src={logoState === 'primary' ? primaryLogo : fallbackLogo}
            onError={handleImageError}
            title={org.name}
            alt=""
          />
        )}
      </div>
      <div className={styles.organizationInfo}>
        <p className={styles.organizationName}>{healthSystem}</p>
        {department && <p className={styles.organizationDepartment}>{department}</p>}
      </div>
    </button>
  );
}

export function RenderOrganizationList() {
  const lang = useSelector(getLang('KNOWLEDGE_HUB'));

  const {
    onSelectOrganization,
    organizationsList: list,
    organizationId,
    loading,
    analyticsTriggers,
  } = useKnowledgeHub();

  useEffect(() => {
    analyticsTriggers?.onPageLoad();
  }, [analyticsTriggers]);

  const { publicOrgs, privateOrgs } = useMemo(() => {
    const [publicOrgs, privateOrgs] = partition(list, org => org?.isPublicContentProvider ?? false);
    // VIEWERs can only see public organizations, not private shared ones
    return { publicOrgs, privateOrgs };
  }, [list]);

  if (organizationId) return null;

  if (loading) {
    return (
      <div className={styles.loader}>
        <Loader />
      </div>
    );
  }

  const hasNoOrganizations = publicOrgs.length === 0 && privateOrgs.length === 0;
  if (hasNoOrganizations) {
    return <NotFound text={lang.NO_HUB_ORGANIZATIONS} />;
  }

  return (
    <div className={styles.organizationListContainer}>
      {publicOrgs.length > 0 && (
        <>
          <Divider style={{ borderColor: colorPalette.neutral5 }} />
          <h3>{lang.OPEN_ACCESS_KNOWLEDGE}</h3>
          <div className={styles.content}>
            {publicOrgs.map(org => (
              <OrganizationCard
                key={org.id}
                org={org}
                lang={lang}
                onSelectOrganization={onSelectOrganization}
              />
            ))}
          </div>
        </>
      )}

      {privateOrgs.length > 0 && (
        <>
          <Divider style={{ borderColor: colorPalette.neutral5 }} />
          <h3>{lang.PEER_INSTITUTION_KNOWLEDGE}</h3>
          <div className={styles.content}>
            {privateOrgs.map(org => (
              <OrganizationCard
                key={org.id}
                org={org}
                lang={lang}
                onSelectOrganization={onSelectOrganization}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
